import React, { useEffect, useRef, useState } from 'react';
import {
  EVButton,
  EVAlert,
  EVGrid,
  EVInputLabel,
  EVIconButton,
  EVPaper,
  EVSlider,
  EVTextField,
  EVZoomInIcon,
  EVZoomOutIcon,
} from 'lib/components';
import { useDispatch, useSelector } from 'lib/redux';
import { useTranslation } from 'lib/translation';
import clsx from 'clsx';
import { ERROR } from 'constants/AppConstants';
import { updateAllFacetsPitch, updateFacetPitch } from '../PitchEditor.actions';
import EditPitchesStyles from './EditPitchesStyles';
import {
  EDIT_PITCH_ZOOM_LIMITS, PITCH_VALUE_REGEX, ZOOM_STEP, isPitchValueValid,
} from '../PitchEditor.utils';

function EditPitches() {
  const classes = EditPitchesStyles();
  const { t } = useTranslation();
  const { standardLineDiagram, reportFacets } = useSelector((state) => state.pitchEditor);
  const imageRef = useRef();
  const dispatch = useDispatch();

  const [zoom, setZoom] = useState(1);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [assignPitch, setAssignPitch] = useState('');

  useEffect(() => {
    const listener = () => {
      setWidth(imageRef.current.naturalWidth * zoom);
      setHeight(imageRef.current.naturalHeight * zoom);
    };
    listener();
    imageRef.current?.addEventListener('load', listener);

    return () => imageRef.current?.removeEventListener('load', listener);
  }, [imageRef, zoom]);

  const handleAssignPitchChange = (event) => {
    const { target: { value } } = event;
    // Restrict input to one decimal place
    if (!value || PITCH_VALUE_REGEX.test(value)) setAssignPitch(value);
  };

  const handleZoomChange = (_, newValue) => setZoom(newValue);
  const handleZoomIn = () => setZoom((prevZoom) => prevZoom + ZOOM_STEP);
  const handleZoomOut = () => setZoom((prevZoom) => prevZoom - ZOOM_STEP);

  const handlePitchChange = (facetId) => (event) => {
    const { target: { value } } = event;
    // Restrict input to one decimal place
    if (!value || PITCH_VALUE_REGEX.test(value)) dispatch(updateFacetPitch(facetId, value));
  };

  const handleAssignPitches = () => dispatch(updateAllFacetsPitch(assignPitch));

  const isAssignPitchInvalid = !isPitchValueValid(assignPitch);

  const isAnyFacetPitchInvalid = reportFacets.some((facet) => !isPitchValueValid(facet.pitch));

  return (
    <>
      <EVPaper className={classes.editPitchesControls}>
        {(isAssignPitchInvalid || isAnyFacetPitchInvalid) && (
          <EVAlert severity={ERROR} classes={{ icon: classes.invalidPitchAlertIcon, message: classes.invalidPitchMsg, root: classes.invalidPitchAlert }}>
            {t('pitch_editor__assign_pitch_helper_text')}
          </EVAlert>
        )}
        <EVGrid className={classes.assignPitchContainer}>
          <EVInputLabel
            htmlFor="assign-pitch-input"
            className={classes.assignPitchLabel}
          >
            {t('pitch_editor__assign_pitch')}
          </EVInputLabel>
          <EVGrid container style={{ gap: 10, width: 'unset' }} wrap="nowrap">
            <EVTextField
              id="assign-pitch-input"
              type="number"
              placeholder="00.0"
              inputProps={{ min: 0, max: 36, step: 0.1 }}
              value={assignPitch}
              onChange={handleAssignPitchChange}
              variant="outlined"
              classes={{ root: clsx(classes.assignPitchInput, isAssignPitchInvalid && 'error') }}
            />
            <EVButton
              id="apply-btn"
              variant="contained"
              className={classes.applyBtn}
              onClick={handleAssignPitches}
              disabled={isAssignPitchInvalid}
            >
              {t('finalize_order__order_summary__apply_promo_code')}
            </EVButton>
          </EVGrid>
          <EVPaper className={classes.zoomSliderContainer}>
            <EVIconButton id="zoom-out" onClick={handleZoomOut} disabled={zoom <= EDIT_PITCH_ZOOM_LIMITS.MIN}>
              <EVZoomOutIcon htmlColor="#007CAD" />
            </EVIconButton>
            <EVSlider
              id="zoom-slider"
              value={zoom}
              onChange={handleZoomChange}
              aria-labelledby="zoom-slider"
              min={EDIT_PITCH_ZOOM_LIMITS.MIN}
              max={EDIT_PITCH_ZOOM_LIMITS.MAX}
              step={0.01}
            />
            <EVIconButton id="zoom-in" onClick={handleZoomIn} disabled={zoom >= EDIT_PITCH_ZOOM_LIMITS.MAX}>
              <EVZoomInIcon htmlColor="#007CAD" />
            </EVIconButton>
          </EVPaper>
        </EVGrid>
      </EVPaper>
      <div style={{ height: imageRef.current?.naturalHeight || 0, overflow: 'scroll' }}>
        <div style={{
          width, height, position: 'relative',
        }}
        >
          <img
            src={`data:image/png;base64,${standardLineDiagram}`}
            alt="test"
            style={{ width: '100%', height: '100%' }}
            ref={imageRef}
          />
          <div style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          }}
          >
            {reportFacets.map((facet) => (
              <input
                key={facet.facetId}
                id={`facet-${facet.facetId}`}
                type="number"
                min={0}
                max={36}
                style={{
                  top: `${facet.hotSpotY}%`,
                  left: `calc(${facet.hotSpotX}% - 20px)`,
                }}
                className={clsx(classes.facetInputs, !isPitchValueValid(facet.pitch) && 'error')}
                value={facet.pitch}
                onChange={handlePitchChange(facet.facetId)}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default EditPitches;
